import PropTypes from 'prop-types';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import getInitialProductLinesSelection from '../../utils/getInitialProductLinesSelection';
import getProductLinesBySiteName from '../../utils/getProductLinesBySiteName';
import useFetchDealers from '../../hooks/useFetchDealers';
import useAppName from '../../hooks/useAppName';
import dealerDataMapper from '../../utils/dealerDataMapper';
import getFilteredDealersList from '../../utils/getFilteredDealersList';
import { SHOW_RADIO_BUTTON, MAP_VIEW_IDENTIFIER, productLineLetter } from '../../utils/constants';
import useJSSComponentContext from '../../hooks/useJSSComponentContext';
import useSetDealerFromCookieOnMount from '../../hooks/useSetDealerFromCookieOnMount';

const DealersListContext = createContext({});

const DealersListProvider = ({ children }) => {
  const siteName = useAppName();
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isDealerModalOpen, setDealerModalOpen] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [hoveredDealer, setHoveredDealer] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [activeView, setActiveView] = useState(MAP_VIEW_IDENTIFIER);

  // current product line set by JSS site name
  const [productLineCode, setProductLineCode] = useState(productLineLetter.get(siteName));
  // product lines selection states
  const productLinesPerBrand = getProductLinesBySiteName(siteName);
  const [productLinesSelection, setProductLinesSelection] = useState(
    getInitialProductLinesSelection(productLinesPerBrand),
  );

  useEffect(() => {
    if (SHOW_RADIO_BUTTON) {
      // set default productLines selection
      setProductLinesSelection(prev => ({
        ...prev,
        ...{ [productLinesPerBrand?.[0]]: true },
      }));
    }
  }, []);

  const { fields, componentName } = useJSSComponentContext();
  const evDealer = componentName && fields?.isVehicleEV;

  const { data: dealersList, error, isLoading } = useFetchDealers(selectedPlace, productLineCode, evDealer);

  const filteredDealersList = useMemo(() => {
    if (Array.isArray(dealersList) && dealersList.length) {
      const mappedDealers = dealersList
        ?.map(dealer => dealerDataMapper(dealer))
        .sort((a, b) => a.drivingDistance - b.drivingDistance);

      // when rendering radio button, disable filtering logic
      if (SHOW_RADIO_BUTTON) {
        return mappedDealers;
      }
      return getFilteredDealersList(productLinesSelection, mappedDealers);
    }
    return [];
  }, [dealersList, productLinesSelection]);

  useSetDealerFromCookieOnMount(filteredDealersList, setSelectedDealer);

  const value = useMemo(
    () => ({
      dealersList: filteredDealersList,
      selectedPlace,
      setSelectedPlace,
      error,
      isLoading,
      isOpen,
      setIsOpen,
      isDealerModalOpen,
      setDealerModalOpen,
      selectedDealer,
      setSelectedDealer,
      hoveredDealer,
      setHoveredDealer,
      inputValue,
      setInputValue,
      setProductLineCode,
      productLinesSelection,
      setProductLinesSelection,
      activeView,
      setActiveView,
    }),
    [
      filteredDealersList,
      selectedPlace,
      error,
      isLoading,
      isOpen,
      isDealerModalOpen,
      selectedDealer,
      hoveredDealer,
      inputValue,
      activeView,
      productLinesSelection,
    ],
  );

  return <DealersListContext.Provider value={value}>{children}</DealersListContext.Provider>;
};

DealersListProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

export { DealersListContext, DealersListProvider };
